import React, { useState, useEffect } from "react";
import {  Modal, Button } from "antd";
import { useMicrosite } from "../../custom-hooks/useMicrosite";
import MicrositeSettingsCopy from "./microsite-settings-copy";
import Preview from "../microSites/edit/preview";
import { useStore } from "../../store";
import MicrositeEdit from "./microsite-edit";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TAB_KEYS } from "../../config";
import { setMicroSiteActions } from "../../store/actions";
import "./style.scss";
import { getMicrositeUrl } from "../../utils/helper";
import AppFeaturesManagement from "../app-features/AppFeaturesManagement";
import MicrositeHeader, {
  isMicrositeAccordionLocked,
} from "./MicrositeHeader";
import {
  clearRosiePendingFlags,
  consumeForceOpenRosieForSite,
  hasRosieWebsiteLink,
  isRosiePendingForSite,
  isSingleMicrositeOrg,
  markForceOpenRosieForSite,
  markRosiePromptShown,
} from "../../utils/rosieNewSite";

export default function MicroSiteAccordian({
  data,
  children,
  activeKey,
  micrositeData,
  setDidProSettingsUpdate
}) {
  const { publishMicroSite } = useMicrosite();
  const [switchState, setSwitchState] = useState(data.published);
  const [publishConfirmation, setPublishConfirmation] = useState(false);
  const [unPublishConfirmation, setUnPublishConfirmation] = useState(false);
  const { settings, dispatch, microSiteActions, microSites } = useStore();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { micrositeId } = useParams();
  const partnerID = localStorage.getItem("partnerID");
  const partner = localStorage.getItem("partner");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Set after manual click, or single-site auto-open (force flag from getMicroSites).
  const [manualRosieKick, setManualRosieKick] = useState(false);

  const isAccordionLocked = isMicrositeAccordionLocked(data?.approvalStatus);
  const isAccordionOpen =
    !isAccordionLocked && String(activeKey) === String(data.id);
  const siteId = String(data?.id ?? "");
  const shouldOpenRosie =
    isAccordionOpen &&
    manualRosieKick &&
    isRosiePendingForSite(siteId) &&
    hasRosieWebsiteLink(data);

  useEffect(() => {
    setSwitchState(data.isPublished);
    localStorage.setItem("micrositeId", data.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Non-approved sites cannot stay "open" via deep link / stale URL.
  useEffect(() => {
    if (!isAccordionLocked) return;
    if (String(activeKey) !== String(data.id)) return;
    navigate(`/`, { state: state ? state : TAB_KEYS.aboutUs, replace: true });
  }, [isAccordionLocked, activeKey, data.id, navigate, state]);

  // Site Features + Rosie:
  // - multi-site: only after manual accordion click (force flag)
  // - single-site + website + pending: also on refresh auto-open
  useEffect(() => {
    if (!isAccordionOpen) {
      setManualRosieKick(false);
      return;
    }

    const forced = consumeForceOpenRosieForSite(siteId);
    const singleSiteAuto =
      isSingleMicrositeOrg(micrositeData) &&
      isRosiePendingForSite(siteId) &&
      hasRosieWebsiteLink(data);

    if (!forced && !singleSiteAuto) return;

    if (!isRosiePendingForSite(siteId)) return;

    if (!hasRosieWebsiteLink(data)) {
      clearRosiePendingFlags();
      markRosiePromptShown(siteId);
      return;
    }

    setManualRosieKick(true);
    const timer = setTimeout(() => {
      setShowSubscriptionModal(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    isAccordionOpen,
    siteId,
    data?.websiteLink,
    data?.website,
    micrositeData,
  ]);

  const handleMenus = (activeKey, menuKey, change) => {
    dispatch(setMicroSiteActions({ activeKey, menuKey, change }));
  };

  const redirectMicroSite = (key) => {
    if (isMicrositeAccordionLocked(data?.approvalStatus)) return;

    const isVisuallyOpen =
      !isMicrositeAccordionLocked(data?.approvalStatus) &&
      String(key) === String(activeKey);

    if (isVisuallyOpen) {
      // Collapse active accordion
      localStorage.setItem("micrositeId", key);
      navigate(`/`, { state: state ? state : TAB_KEYS.aboutUs });
      return;
    }

    // Manual accordion open → allow Site Features + Rosie for this pending site only.
    if (hasRosieWebsiteLink(data) && String(key) === siteId) {
      markForceOpenRosieForSite(key);
    }

    // Switch site directly — avoid / → /:id bounce that remounts the page
    // and retriggers get-tabs-settings multiple times.
    localStorage.setItem("micrositeId", key);
    navigate(`/${key}`, {
      state: state ? state : TAB_KEYS.aboutUs,
    });
  };

  const handleHelpClick = () => {
    if (localStorage.getItem("tab")) {
      localStorage.setItem("page", "help");
      navigate("/help");
    }
  };

  const handleCancel = () => {
    setSwitchState(microSites[0].isPublished);
    setPublishConfirmation(false);
    setUnPublishConfirmation(false);
  };

  const handleOk = () => {
    publishHandler(true);
  };

  const unPublishOk = () => {
    publishHandler(false);
  };

  const publishHandler = (value) => {
    if (value === true) {
      publishMicroSite(data.id, "published");
      setPublishConfirmation(false);
      if (localStorage.getItem("switchSuccess")) {
        setSwitchState(true);
      } else {
        setSwitchState(false);
      }
      setTimeout(() => {
        localStorage.removeItem("switchSuccess");
      }, 2000);
    } else {
      publishMicroSite(data.id, "unpublished");
      setUnPublishConfirmation(false);
      if (localStorage.getItem("switchSuccess")) {
        setSwitchState(false);
      } else {
        setSwitchState(true);
      }
      setTimeout(() => {
        localStorage.removeItem("switchSuccess");
      }, 2000);
    }
  };

  const [settingsModal, setSettingsModal] = useState(false);
  const handleSettings = () => {
    setSettingsModal(false);
    setDidProSettingsUpdate((prev) => !prev)
  }

  return (
    <div className="ms-acc-main">
      <div className="ms-accordian">
        {microSiteActions &&
          microSiteActions[data.id] &&
          microSiteActions[data.id]["edit"] &&
          microSiteActions[data.id]["edit"] ? (
          <div className="microsite-edit-header">
            <MicrositeEdit
              page={"home"}
              data={data}
              closeSettings={() => handleMenus(data.id, "edit", false)}
            />
          </div>
        ) : (
          <MicrositeHeader
            data={data}
            activeKey={activeKey}
            micrositeData={micrositeData}
            micrositeId={micrositeId}
            switchState={switchState}
            partner={partner}
            partnerID={partnerID}
            navigate={navigate}
            redirectMicroSite={redirectMicroSite}
            handleMenus={handleMenus}
            handleHelpClick={handleHelpClick}
            setShowSubscriptionModal={setShowSubscriptionModal}
            setPublishConfirmation={setPublishConfirmation}
            setUnPublishConfirmation={setUnPublishConfirmation}
            getMicrositeUrl={getMicrositeUrl}
          />
        )}
        {isAccordionOpen ? (
          <div className="ms-accordian-content">{children}</div>
        ) : (
          <></>
        )}
        {settingsModal ? (
          <MicrositeSettingsCopy
            activeKey={data.id}
            open={settingsModal}
            closeSettings={() => handleSettings()}
          />
        ) : (
          <></>
        )}
        {microSiteActions &&
          microSiteActions[data.id] &&
          microSiteActions[data.id]["preview"] &&
          microSiteActions[data.id]["preview"] ? (
          <Preview
            micrositeId={data.id}
            siteSetting={settings}
            closeSettings={() => handleMenus(data.id, "preview", false)}
            data={data}
          />
        ) : (
          <></>
        )}
      </div>
      <Modal
        className="confirmation-modal"
        open={publishConfirmation}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose={true}
        closable={false}
        footer={null}
      >
        <div className="message">Are you sure you want to go online?</div>
        <div className="modalBtns">
          <Button className="cancel-btn" type="primary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button className="confirm-btn" key="back" onClick={handleOk}>
            Publish
          </Button>
        </div>
      </Modal>
      <Modal
        className="confirmation-modal"
        open={unPublishConfirmation}
        onOk={unPublishOk}
        destroyOnClose={true}
        closable={false}
        onCancel={handleCancel}
        footer={null}
      >
        <div className="message">Are you sure you want to go offline?</div>
        <div className="modalBtns">
          <Button className="cancel-btn" key="back" onClick={unPublishOk}>
            Unpublish
          </Button>
          <Button className="confirm-btn" type="primary" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </Modal>

      {data?.id && (
        <AppFeaturesManagement
          micrositeData={data}
          micrositeId={data?.id}
          shouldOpenRosie={shouldOpenRosie}
          showSubscriptionModal={showSubscriptionModal}
          setShowSubscriptionModal={setShowSubscriptionModal}
          setDidProSettingsUpdate={setDidProSettingsUpdate}
        />
      )}
    </div>
  );
}
